import { dsHttpOrigin } from "../online/net";
import { decodeReplayFile } from "core/src/core/replay-file.js";

// A finished online match, as players may watch it (edge-server replay-api.js:
// GET /api/replays/file?match=<id>, the copy shaped for players). Matches are kept
// for REPLAY_DAYS; after that the link simply says so.

export const REPLAY_DAYS = 30;
// Replays are stored from this moment on; an older match has none.
export const REPLAYS_SINCE = Date.parse("2026-09-20T21:00:00Z");

export interface PlayerReplay { meta: any; match: any }

// Whether a match of this date can still have a replay (for showing a Watch link).
export function replayMayExist(createdAt: string | number): boolean {
  const t = typeof createdAt === "number" ? createdAt : Date.parse(createdAt);
  return Number.isFinite(t) && t >= REPLAYS_SINCE && Date.now() - t < REPLAY_DAYS * 24 * 3600 * 1000;
}

// A match old enough that its replay has been deleted (as opposed to one played
// before replays were recorded at all, which never had one).
export function replayExpired(createdAt: string | number): boolean {
  const t = typeof createdAt === "number" ? createdAt : Date.parse(createdAt);
  return Number.isFinite(t) && t >= REPLAYS_SINCE && Date.now() - t >= REPLAY_DAYS * 24 * 3600 * 1000;
}

// you: the viewer's own seat in that match, when the link comes from their profile.
export function replayPath(matchId: string, you?: string | null): string {
  return "/replay?match=" + encodeURIComponent(matchId) + (you ? "&you=" + encodeURIComponent(you) : "");
}

// `server`: the ?server= override of a dev session (the replay lives on the server
// the match was played on); players never have one.
export async function fetchReplay(matchId: string, server?: string | null): Promise<PlayerReplay | { error: "gone" | "network" | "format" }> {
  try {
    const res = await fetch(`${dsHttpOrigin(server)}/api/replays/file?match=${encodeURIComponent(matchId)}`);
    if (res.status === 404) return { error: "gone" };
    if (!res.ok) return { error: "network" };
    const decoded = decodeReplayFile(await res.json());
    return decoded ? { meta: decoded.meta, match: decoded.match } : { error: "format" };
  } catch {
    return { error: "network" };
  }
}

// A match's poster (core replay-poster.js): the few KB a list draws a match from.
export interface ReplayPoster {
  v: number;
  // `shape` is missing on posters saved before it (those carry only `round`).
  map: { name: string | null; width: number; height: number; round: boolean; shape?: "rectangle" | "circle" | "stadium" };
  rounds: { winner: string | null; order: string[] }[];
  seats: { id: string; colorId: string; name: string; tag?: string }[];
  trails: { c: string | null; l: number[][] }[];
}

const posterCache = new Map<string, Promise<ReplayPoster | null>>();
// null = this match has no poster (it still lists, just without its arena).
export function fetchPoster(matchId: string, server?: string | null): Promise<ReplayPoster | null> {
  let hit = posterCache.get(matchId);
  if (!hit) {
    hit = fetch(`${dsHttpOrigin(server)}/api/replays/poster?match=${encodeURIComponent(matchId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((p) => (p && p.v === 1 && Array.isArray(p.trails) ? (p as ReplayPoster) : null))
      .catch(() => null);
    posterCache.set(matchId, hit);
  }
  return hit;
}

// Debug fixtures (/debug/matches): put a poster in the cache without a server.
export function primePoster(matchId: string, poster: ReplayPoster | null): void {
  posterCache.set(matchId, Promise.resolve(poster));
}
